import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { isParlourOpen } from '@/lib/business'
import { hours } from '@/constants/site'

interface OpenStatusProps {
    tone?: 'light' | 'dark'
    className?: string
}

type Status = 'open' | 'closed' | null

// Renders a neutral "open all 7 days" label on the server, then switches to a
// live open or closed badge (India time) once the page has loaded.
export const OpenStatus: FC<OpenStatusProps> = ({ tone = 'light', className }): ReactNode => {
    const [status, setStatus] = useState<Status>(null)

    useEffect(() => {
        const update = () => setStatus(isParlourOpen() ? 'open' : 'closed')

        update()
        const timer = window.setInterval(update, 60 * 1000)

        return () => window.clearInterval(timer)
    }, [])

    const isDark = tone === 'dark'

    const label = status === 'open'
        ? `Open now, until ${hours.closesLabel}`
        : status === 'closed'
            ? `Closed now, opens ${hours.opensLabel}`
            : hours.days

    return (
        <span
            className={cn(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium',
                isDark ? 'border-gold/30 bg-white/5 text-ivory/90' : 'border-border bg-white text-foreground',
                className
            )}
            aria-live='polite'
        >
            <span
                aria-hidden='true'
                className={cn(
                    'relative flex size-2 rounded-full',
                    status === 'open' && 'bg-emerald-500',
                    status === 'closed' && 'bg-rose-400',
                    status === null && 'bg-gold'
                )}
            >
                {status === 'open' && (
                    <span className='absolute inset-0 animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:hidden' />
                )}
            </span>

            {label}
        </span>
    )
}
